import { useMemo, useState } from 'react';
import type { NotificationChannel } from '@wapharma/shared';
import { notificationChannelLabel } from '@wapharma/shared';
import {
  ConversationThread,
  Input,
  Select,
  Stack,
  Text,
  TextArea,
  type ConversationMessageItem,
} from '@wapharma/design-system';
import { apiPost } from '../api';
import { getDisplayError } from '../apiErrors';
import { AsyncActionButton } from './AsyncActionButton';
import { FriendlyDate } from './FriendlyDate';
import { useConversation } from '../hooks/useConversation';

export type StaffContactChannel = {
  channel: NotificationChannel;
  label: string;
  destination: string | null;
  available: boolean;
  unavailableReason: string | null;
};

type StaffMessageComposerProps = {
  userId: string;
  token: string | null;
  disabled?: boolean;
  contactChannels: StaffContactChannel[];
  initialChannel?: NotificationChannel;
  lockChannel?: boolean;
  onSent?: () => void;
  /** Default `/admin/users/:id/messages`. */
  messagesPath?: string;
  bodyPlaceholder?: string;
};

export function StaffMessageComposer({
  userId,
  token,
  disabled,
  contactChannels,
  initialChannel,
  lockChannel,
  onSent,
  messagesPath,
  bodyPlaceholder = 'Votre message au collaborateur…',
}: StaffMessageComposerProps) {
  const { conversation, refresh } = useConversation({
    entityType: 'AuthAccount',
    entityId: userId,
    audience: 'CLIENT',
    token,
  });

  const threadMessages = useMemo<ConversationMessageItem[]>(
    () =>
      (conversation?.messages ?? []).map((m) => ({
        ...m,
        direction: m.direction ?? 'OUTBOUND',
      })),
    [conversation?.messages],
  );

  const availableChannels = useMemo(
    () => contactChannels.filter((c) => c.available),
    [contactChannels],
  );
  const [channel, setChannel] = useState<NotificationChannel>(
    initialChannel ?? availableChannels[0]?.channel ?? 'EMAIL',
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const activeChannel =
    availableChannels.find((c) => c.channel === channel) ?? availableChannels[0] ?? null;

  const channelOptions = contactChannels.map((c) => ({
    value: c.channel,
    label: c.available
      ? `${c.label}${c.destination ? ` — ${c.destination}` : ''}`
      : `${c.label} (indisponible)`,
    disabled: !c.available,
  }));

  if (!contactChannels.length) {
    return (
      <Text size="sm" c="dimmed">
        Aucun canal de communication configuré.
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {threadMessages.length > 0 ? (
        <ConversationThread
          messages={threadMessages}
          renderTimestamp={(iso) => <FriendlyDate iso={iso} />}
        />
      ) : null}
      {!lockChannel ? (
        <Select
          label="Canal"
          value={activeChannel?.channel ?? channel}
          onChange={(e) => setChannel(e.currentTarget.value as NotificationChannel)}
          options={channelOptions}
          disabled={disabled || availableChannels.length === 0}
        />
      ) : null}
      {activeChannel && !activeChannel.available ? (
        <Text size="xs" c="dimmed">
          {activeChannel.unavailableReason}
        </Text>
      ) : null}
      {contactChannels
        .filter((c) => !c.available && c.unavailableReason)
        .map((c) => (
          <Text key={c.channel} size="xs" c="dimmed">
            {notificationChannelLabel(c.channel)} : {c.unavailableReason}
          </Text>
        ))}
      {channel === 'EMAIL' ? (
        <Input
          label="Objet"
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
          disabled={disabled || !activeChannel?.available}
          placeholder="Optionnel"
        />
      ) : null}
      <TextArea
        label="Message"
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
        disabled={disabled || !activeChannel?.available}
        rows={5}
        placeholder={bodyPlaceholder}
      />
      {err ? (
        <Text size="sm" c="error">
          {err}
        </Text>
      ) : null}
      <div>
        <AsyncActionButton
          type="button"
          variant="primary"
          disabled={disabled || !token || !activeChannel?.available || !body.trim()}
          pendingLabel="Envoi…"
          onAction={async () => {
            if (!token || !activeChannel?.available) return;
            setErr(null);
            const path = messagesPath ?? `/admin/users/${userId}/messages`;
            try {
              await apiPost(
                path,
                {
                  channel: activeChannel.channel,
                  subject: subject.trim() || null,
                  body: body.trim(),
                },
                token,
              );
              setBody('');
              setSubject('');
              await refresh();
              onSent?.();
            } catch (e: unknown) {
              setErr(getDisplayError(e));
              throw e;
            }
          }}
        >
          Envoyer
        </AsyncActionButton>
      </div>
    </Stack>
  );
}
